/**
 * serviceWorkerRegistration.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Registers /sw.js (served from the public/ folder) as the app's service worker.
 *
 * Called once from src/index.tsx:
 *   import { registerSW } from "./serviceWorkerRegistration";
 *   registerSW();
 *
 * HOW IT WORKS:
 *   1. The browser downloads /sw.js once.
 *   2. The SW installs and pre-caches the app shell.
 *   3. On every subsequent load the SW intercepts network requests and applies
 *      the caching strategies described in public/sw.js.
 *   4. When a new version of sw.js is deployed, the browser downloads it,
 *      installs it in the background, and activates it on the next page load
 *      (or immediately if we post SKIP_WAITING).
 * ─────────────────────────────────────────────────────────────────────────────
 */

type SWCallbacks = {
  onUpdate?:  (registration: ServiceWorkerRegistration) => void;
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
};

export function registerSW(callbacks: SWCallbacks = {}): void {
  // Only register in production-like environments (or locally via HTTPS / localhost)
  if (!("serviceWorker" in navigator)) return;

  window.addEventListener("load", () => {
    const swUrl = `${process.env.PUBLIC_URL}/sw.js`;

    navigator.serviceWorker
      .register(swUrl)
      .then((registration) => {
        // ── Already have a SW and a waiting update ──────────────────────────
        registration.onupdatefound = () => {
          const newWorker = registration.installing;
          if (!newWorker) return;

          newWorker.onstatechange = () => {
            if (newWorker.state !== "installed") return;

            if (navigator.serviceWorker.controller) {
              // A new SW is waiting — notify the app so it can show
              // "Update available — reload to apply" toast
              callbacks.onUpdate?.(registration);

              // Immediately activate the new worker (skip waiting)
              newWorker.postMessage({ type: "SKIP_WAITING" });
            } else {
              // First-time install — app is now fully cached
              callbacks.onSuccess?.(registration);
              console.info("[AMS PWA] App cached for offline use.");
            }
          };
        };
      })
      .catch((err) => {
        console.error("[AMS PWA] Service worker registration failed:", err);
      });

    // ── When a new SW takes control, reload to load fresh assets ───────────
    let refreshing = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (!refreshing) {
        refreshing = true;
        window.location.reload();
      }
    });
  });
}

export function unregisterSW(): void {
  if (!("serviceWorker" in navigator)) return;
  navigator.serviceWorker.ready
    .then((reg) => reg.unregister())
    .catch(console.error);
}
